import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { Quote, SimilarExample } from '@furni/shared';
import { AddToOrderSheet } from '../components/AddToOrderSheet';
import { Card, Ornament } from '../components/Card';
import { Shell } from '../components/Shell';
import { ShareButton } from '../components/ShareButton';
import { fetchQuote } from '../lib/api';
import { cart, useCart } from '../lib/cart';
import {
  formatNumber,
  formatSAR,
  formatSARFrom,
  formatSARVatInc,
  withVat,
} from '../lib/format';
import { useCopy, useSetting } from '../lib/settings';

export const Route = createFileRoute('/quote/$id')({
  component: QuotePage,
});

function QuotePage() {
  const { t } = useTranslation();
  const { id } = useParams({ from: '/quote/$id' });
  const { data, isLoading, error } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => fetchQuote(id),
  });

  if (isLoading) {
    return (
      <Shell>
        <div className="pt-10 space-y-4">
          <div className="shimmer-bg animate-shimmer h-40 rounded-3xl" />
          <div className="shimmer-bg animate-shimmer h-24 rounded-3xl" />
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell showBack>
        <div className="pt-10 text-oud-800 text-center">
          {error instanceof Error ? error.message : t('upload.errors.generic')}
        </div>
      </Shell>
    );
  }

  return <QuoteView quote={data} />;
}

function QuoteView({ quote }: { quote: Quote }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = useCart();
  const [sheetOpen, setSheetOpen] = useState(false);
  const existing = items.find((i) => i.quote_id === quote.id);
  const { analysis, image_url, custom_price_sar } = quote;
  const minQty = useSetting('pricing.minQuantity');

  return (
    <Shell
      className="pb-40"
      rightSlot={
        <ShareButton
          title={t('quote.share_title', {
            type: analysis.type,
            price: formatNumber(withVat(custom_price_sar)),
          })}
          text={t('quote.share_text')}
          url={`/quote/${quote.id}`}
        />
      }
    >
      <div className="pt-4 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative rounded-3xl overflow-hidden shadow-quote border border-oud-100 bg-oud-900"
        >
          <img
            src={image_url}
            alt={analysis.type}
            className="w-full h-64 object-cover"
            loading="eager"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-oud-900/90 via-oud-900/50 to-transparent p-4 pt-12">
            <p className="text-[10px] tracking-[0.25em] text-gold-soft font-bold uppercase">
              {t('quote.detected')}
            </p>
            <h1 className="text-xl font-black text-sand mt-1 leading-tight">
              {analysis.type}
              {analysis.style ? (
                <span className="text-cream/80 font-bold"> · {analysis.style}</span>
              ) : null}
            </h1>
            {analysis.estimated_dimensions_cm ? (
              <p className="text-xs text-cream/70 mt-1">
                {t('quote.dimensions', {
                  w: analysis.estimated_dimensions_cm.width ?? '—',
                  d: analysis.estimated_dimensions_cm.depth ?? '—',
                  h: analysis.estimated_dimensions_cm.height ?? '—',
                })}
              </p>
            ) : null}
          </div>
        </motion.div>

        <PriceHero
          custom={custom_price_sar}
          marketAvg={quote.market_avg_sar}
        />

        {analysis.similar_examples.length > 0 ? (
          <section>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-sm font-bold text-oud-700 tracking-widest">
                {t('quote.examples_title')}
              </h3>
              <Ornament className="flex-1" />
            </div>
            <div className="grid grid-cols-1 gap-3">
              {analysis.similar_examples.slice(0, 6).map((ex) => (
                <ExampleCard key={`${ex.title}-${ex.url ?? ''}`} example={ex} />
              ))}
            </div>
          </section>
        ) : null}

      </div>

      <div className="fixed inset-x-0 bottom-0 bg-sand/95 backdrop-blur border-t border-oud-100/60 z-20">
        <div className="max-w-md mx-auto px-5 py-3 space-y-2">
          {minQty > 1 ? (
            <p className="text-center text-[11px] text-amber-700 font-bold">
              الحد الأدنى للطلب: {formatNumber(minQty)} قطع (تراكمي)
            </p>
          ) : null}
          {existing ? (
            <p className="text-center text-[11px] text-sage-deep font-bold">
              {t('quote.already_in_cart', { qty: formatNumber(existing.qty) })}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl font-bold h-14 px-8 text-lg bg-oud-800 text-sand shadow-soft hover:bg-oud-900 hover:shadow-quote transition-all active:scale-[0.98] w-full"
          >
            <PlusIcon className="w-5 h-5" />
            {t('quote.cta')}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: items.length > 0 ? '/cart' : '/' })}
            className="block w-full text-sm text-oud-600 hover:text-oud-900 font-bold py-2 rounded-xl hover:bg-cream transition-colors"
          >
            {items.length > 0 ? t('quote.cta_view_cart') : t('quote.cta_scan_another')}
          </button>
        </div>
      </div>

      <AddToOrderSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        initial={existing ? { qty: existing.qty, notes: existing.notes } : undefined}
        onSubmit={({ qty, notes }) => {
          cart.add({ quote_id: quote.id, qty, notes });
          void navigate({ to: '/cart' });
        }}
      />
    </Shell>
  );
}

function PriceHero({
  custom,
  marketAvg,
}: {
  custom: number;
  marketAvg: number;
}) {
  const { t } = useTranslation();
  const yourPriceLabel = useCopy('copy.yourPrice');
  const marketLabel = useCopy('copy.marketPrice');
  const savingsLabel = useCopy('copy.savingsLabel');
  const customWithVat = withVat(custom);
  const savings = Math.max(0, marketAvg - customWithVat);
  const savingsPct = marketAvg > 0 ? Math.round((savings / marketAvg) * 100) : 0;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.15, duration: 0.45 }}
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-oud-800 to-oud-900 text-sand p-6 shadow-quote"
    >
      <div className="absolute inset-0 najdi-pattern opacity-20 pointer-events-none" aria-hidden />
      <div className="relative space-y-4">
        <div>
          <p className="text-xs tracking-[0.3em] text-gold-soft font-bold uppercase">
            {yourPriceLabel}
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-5xl font-black tracking-tight text-gold-soft">
              {formatSARVatInc(custom)}
            </span>
          </div>
          <p className="text-[11px] text-oud-100/50 mt-1.5 italic">
            {t('quote.estimate_note')}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <HintChip>{t('quote.quantity_hint')}</HintChip>
          <HintChip>{t('quote.size_hint')}</HintChip>
        </div>

        <div className="pt-4 border-t border-oud-100/10 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] tracking-wider text-oud-100/60 font-bold uppercase mb-1">
                {marketLabel}
              </p>
              <p className="text-xl font-bold text-oud-100/90 tabular-nums tracking-tight">
                <span className="line-through decoration-oud-100/40 decoration-[1.5px]">
                  {formatSAR(marketAvg)}
                </span>
              </p>
            </div>
            <div className="flex-none text-center rounded-2xl bg-gradient-to-br from-sage/30 to-sage-deep/40 border border-sage-soft/50 px-4 py-2.5 shadow-inner">
              <p className="text-[9px] tracking-[0.2em] text-sage-soft/80 font-bold uppercase">
                {savingsLabel}
              </p>
              <p className="text-3xl font-black text-sage-soft leading-none tabular-nums mt-0.5">
                {savingsPct}
                <span className="text-xl">٪</span>
              </p>
              <p className="text-[10px] text-sage-soft/90 font-bold mt-0.5 tabular-nums">
                −{formatSAR(savings)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ExampleCard({ example }: { example: SimilarExample }) {
  const { t } = useTranslation();
  const searchUrl = buildSearchUrl(example);

  return (
    <a
      href={searchUrl}
      target="_blank"
      rel="noreferrer noopener"
      className="block group"
    >
      <Card className="flex items-stretch gap-4 overflow-hidden p-0 group-hover:border-oud-200 transition-colors">
        <div className="flex-none w-24 bg-oud-50 overflow-hidden relative">
          {example.image_url ? (
            <img
              src={example.image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                (e.currentTarget.parentElement as HTMLElement).classList.add('image-failed');
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <div className="absolute inset-0 flex items-center justify-center text-oud-200 [.image-failed_&]:flex">
            <ChairGlyph className="w-8 h-8" />
          </div>
        </div>
        <div className="flex-1 min-w-0 py-3 pl-4 flex flex-col justify-center">
          <h4 className="font-bold text-oud-900 line-clamp-2 text-sm leading-snug">{example.title}</h4>
          {example.retailer ? (
            <p className="text-[11px] text-oud-600 mt-0.5 truncate">
              {t('quote.from_retailer', { retailer: example.retailer })}
            </p>
          ) : null}
          {example.price_sar ? (
            <p className="text-sm font-bold text-gold-deep mt-1">
              {formatSARFrom(example.price_sar)}
            </p>
          ) : null}
        </div>
        <div className="flex items-center pl-4 text-oud-500 group-hover:text-oud-800 transition-colors">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
        </div>
      </Card>
    </a>
  );
}

function buildSearchUrl(ex: SimilarExample): string {
  const parts = [ex.retailer, ex.title].filter(Boolean).join(' ');
  return `https://www.google.com/search?q=${encodeURIComponent(parts)}&hl=ar&gl=sa`;
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className={className} aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function HintChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 border border-gold-soft/30 px-2.5 py-1 text-[11px] text-gold-soft font-bold">
      <span className="w-1 h-1 rounded-full bg-gold-soft" aria-hidden />
      {children}
    </span>
  );
}

function ChairGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 10V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4" />
      <path d="M3 10h18v6H3z" />
      <path d="M5 16v4M19 16v4" />
    </svg>
  );
}

